/**
 * Debug utility for comprehensive application debugging
 * Only logs when REACT_APP_DEBUG=true in environment
 */
import { logger } from '../store/middleware/consoleEventLogger';

const isDebugEnabled = process.env.REACT_APP_DEBUG === 'true';

class DebugLogger {
  constructor() {
    this.sessionId = this.generateSessionId();
    this.startTime = Date.now();
    
    if (isDebugEnabled) {
      logger.log(`🐛 DEBUG MODE ENABLED - Session: ${this.sessionId}`);
      logger.log(`🐛 Environment Variables:`, {
        REACT_APP_REGION: process.env.REACT_APP_REGION,
        REACT_APP_IDENTITY_POOL_ID: process.env.REACT_APP_IDENTITY_POOL_ID,
        REACT_APP_BASE_URL: process.env.REACT_APP_BASE_URL,
        REACT_APP_DEBUG: process.env.REACT_APP_DEBUG
      });
    }
  }

  generateSessionId() {
    return Math.random().toString(36).substring(2, 15);
  }

  getTimestamp() {
    const elapsed = Date.now() - this.startTime;
    return `[${elapsed}ms]`;
  }

  // Component lifecycle debugging
  componentMount(componentName, props = {}) {
    if (!isDebugEnabled) return;
    logger.group(`🔄 ${this.getTimestamp()} Component Mount: ${componentName}`);
    logger.log('Props:', props);
    logger.log('Location:', window.location.href);
    logger.groupEnd();
  }

  componentUnmount(componentName) {
    if (!isDebugEnabled) return;
    logger.log(`🔄 ${this.getTimestamp()} Component Unmount: ${componentName}`);
  }

  // State changes
  stateChange(componentName, stateName, oldValue, newValue) {
    if (!isDebugEnabled) return;
    logger.group(`📊 ${this.getTimestamp()} State Change: ${componentName}.${stateName}`);
    logger.log('Old Value:', oldValue);
    logger.log('New Value:', newValue);
    logger.groupEnd();
  }

  // API calls
  apiRequest(method, url, payload = null, headers = {}) {
    if (!isDebugEnabled) return;
    logger.group(`🌐 ${this.getTimestamp()} API Request: ${method} ${url}`);
    logger.log('Payload:', payload);
    logger.log('Headers:', headers);
    logger.groupEnd();
  }

  apiResponse(method, url, status, data, error = null) {
    if (!isDebugEnabled) return;
    const statusIcon = status >= 200 && status < 300 ? '✅' : '❌';
    logger.group(`${statusIcon} ${this.getTimestamp()} API Response: ${method} ${url} (${status})`);
    if (error) {
      logger.error('Error:', error);
    } else {
      logger.log('Data:', data);
    }
    logger.groupEnd();
  }

  // AWS/Camera specific debugging
  awsCredentials(credentials) {
    if (!isDebugEnabled) return;
    logger.group(`🔐 ${this.getTimestamp()} AWS Credentials`);
    logger.log('Access Key ID:', credentials.accessKeyId ? '***' + credentials.accessKeyId.slice(-4) : 'None');
    logger.log('Region:', credentials.region || 'Not set');
    logger.log('Identity Pool ID:', process.env.REACT_APP_IDENTITY_POOL_ID);
    logger.groupEnd();
  }

  cameraAccess(event, details = {}) {
    if (!isDebugEnabled) return;
    const eventIcons = {
      'request': '📹',
      'granted': '✅',
      'denied': '❌',
      'error': '🚨'
    };
    logger.group(`${eventIcons[event] || '📹'} ${this.getTimestamp()} Camera Access: ${event}`);
    logger.log('Details:', details);
    logger.log('Navigator mediaDevices available:', !!navigator.mediaDevices);
    logger.log('getUserMedia available:', !!navigator.mediaDevices?.getUserMedia);
    logger.groupEnd();
  }

  faceLivenessEvent(event, data = {}) {
    if (!isDebugEnabled) return;
    const eventIcons = {
      'session-created': '🆕',
      'detector-mounted': '🔄',
      'analysis-start': '🔍',
      'analysis-complete': '✅',
      'error': '❌',
      'user-cancel': '🚫'
    };
    logger.group(`${eventIcons[event] || '🔍'} ${this.getTimestamp()} Face Liveness: ${event}`);
    logger.log('Data:', data);
    logger.groupEnd();
  }

  // Navigation debugging
  navigation(from, to, state = {}) {
    if (!isDebugEnabled) return;
    logger.group(`🧭 ${this.getTimestamp()} Navigation: ${from} → ${to}`);
    logger.log('State:', state);
    logger.log('Full URL:', window.location.href);
    logger.groupEnd();
  }

  // Error debugging
  error(context, error, additionalInfo = {}) {
    if (!isDebugEnabled) return;
    logger.group(`🚨 ${this.getTimestamp()} Error in ${context}`);
    logger.error('Error:', error);
    logger.log('Error Name:', error.name);
    logger.log('Error Message:', error.message);
    logger.log('Stack:', error.stack);
    logger.log('Additional Info:', additionalInfo);
    logger.groupEnd();
  }

  // Performance debugging
  performance(label, startTime) {
    if (!isDebugEnabled) return;
    const duration = Date.now() - startTime;
    logger.log(`⏱️ ${this.getTimestamp()} Performance: ${label} took ${duration}ms`);
  }

  // Local storage debugging
  localStorage(action, key, value = null) {
    if (!isDebugEnabled) return;
    logger.group(`💾 ${this.getTimestamp()} LocalStorage: ${action} ${key}`);
    if (action === 'get') {
      logger.log('Value:', localStorage.getItem(key));
    } else if (action === 'set') {
      logger.log('Value:', value);
    }
    logger.groupEnd();
  }

  // Session debugging
  sessionInfo(sessionId, awsSessionId, keys = {}) {
    if (!isDebugEnabled) return;
    logger.group(`🎫 ${this.getTimestamp()} Session Information`);
    logger.log('Session ID:', sessionId);
    logger.log('AWS Session ID:', awsSessionId);
    logger.log('Keys:', {
      identityId: keys.identityId ? '***' + keys.identityId.slice(-8) : 'None',
      accessKeyId: keys.accessKeyId ? '***' + keys.accessKeyId.slice(-4) : 'None'
    });
    logger.groupEnd();
  }

  // DOM debugging
  domEvent(event, element, details = {}) {
    if (!isDebugEnabled) return;
    logger.group(`🖱️ ${this.getTimestamp()} DOM Event: ${event}`);
    logger.log('Element:', element);
    logger.log('Details:', details);
    logger.groupEnd();
  }

  // Custom debug message
  log(message, data = null) {
    if (!isDebugEnabled) return;
    if (data) {
      logger.group(`🐛 ${this.getTimestamp()} ${message}`);
      logger.log('Data:', data);
      logger.groupEnd();
    } else {
      logger.log(`🐛 ${this.getTimestamp()} ${message}`);
    }
  }

  // Browser info
  browserInfo() {
    if (!isDebugEnabled) return;
    logger.group(`🌐 ${this.getTimestamp()} Browser Information`);
    logger.log('User Agent:', navigator.userAgent);
    logger.log('Platform:', navigator.platform);
    logger.log('Language:', navigator.language);
    logger.log('Online:', navigator.onLine);
    logger.log('Cookies Enabled:', navigator.cookieEnabled);
    logger.log('Screen Resolution:', `${window.screen.width}x${window.screen.height}`);
    logger.log('Viewport:', `${window.innerWidth}x${window.innerHeight}`);
    logger.groupEnd();
  }
}

// Create singleton instance
const debugLogger = new DebugLogger();

export default debugLogger;
